import { useState, useEffect } from 'react';
import { Layout } from '../layouts/Layout';
import { Sidebar } from '../components/Sidebar';
import { Header } from '../components/Header';
import { DatePickerCard } from '../components/DatePickerCard';
import VitalSignsDeviceList from '../components/VitalSignsDeviceList';
import HealthChart from '../components/HealthChart';

import { useDevices } from '../hooks/useDevices';
import { getTodayDate } from '../utils/dateUtils';

/**
 * Vital Signs page component
 * Displays health monitoring data with device list, health chart, and statistics
 * Uses useDevices and useHealthData hooks for data fetching
 * Manages device selection and date state
 */
export default function VitalSigns() {
  const { devices } = useDevices();
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(getTodayDate());

  // Auto-select first device when devices are loaded
  useEffect(() => {
    if (devices.length > 0 && !selectedDeviceId && devices[0]?.id) {
      setSelectedDeviceId(devices[0].id);
    }
  }, [devices, selectedDeviceId]);

  // Handle device selection from VitalSignsDeviceList
  useEffect(() => {
    const handleDeviceSelect = (e: CustomEvent) => {
      if (e.detail?.deviceId) {
        setSelectedDeviceId(e.detail.deviceId);
      }
    };

    document.addEventListener('device-select', handleDeviceSelect as EventListener);

    return () => {
      document.removeEventListener('device-select', handleDeviceSelect as EventListener);
    };
  }, []);

  // Handle date changes from DatePickerCard
  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    // Note: HealthChart and VitalSignsDeviceList listen to 'datepicker-range-end' event directly
  };

  return (
    <Layout>
      <Sidebar />
      <Header title="Vital Signs" />

      <main className="ml-0 md:ml-64 pt-20 p-6">
        <div className="grid grid-cols-1 gap-6">
          {/* Date Picker Card */}
          <DatePickerCard 
            onDateChange={handleDateChange}
            initialDate={selectedDate}
          />

          {/* Users and Health Tracking */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <VitalSignsDeviceList
              devices={devices}
              className="lg:col-span-5 xl:col-span-4"
            />
            <HealthChart
              selectedDeviceId={selectedDeviceId || undefined}
              className="lg:col-span-7 xl:col-span-8"
            />
          </div>
        </div>
      </main>
    </Layout>
  );
}
